// Página de retorno del login federado (Google/Apple) — ruta `/auth/callback`.
//
// Cognito (Hosted UI) redirige aquí con `?code=...&state=...` tras el consenti-
// miento del proveedor. Intercambiamos el `code` por tokens (PKCE) con
// `completeFederatedLogin`, guardamos la sesión con `setSession` (idéntico al
// login por contraseña) y redirigimos a la app. Si algo falla, mostramos el
// error y un enlace para reintentar desde el login.
//
// IMPORTANTE (carrera con el provisioning): para un usuario federado NUEVO, el
// trigger PostConfirmation crea su fila `app_user` de forma asíncrona. Igual que
// el auto-registro, tras guardar la sesión dejamos que `AuthContext` cargue los
// permisos; si la primera carga diera 401 `unknown_user`, el propio contexto
// reintenta al refrescar. Para suavizar ese borde, sondeamos brevemente
// `/me/permissions` antes de navegar.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.js';
import { useLanguage } from '../i18n/LanguageProvider.js';
import { completeFederatedLogin } from '../auth/oauth.js';
import { ApiClient, ApiError } from '../api/client.js';
import { createApi } from '../api/endpoints.js';
import { config } from '../config.js';
import { fetchLegalDocuments, recordLegalConsent } from '../marketing/publicApi.js';
import { trackEvent } from '../analytics/analytics.js';
import { Spinner, AlertMessage } from '../ui/components.js';

/** Decodifica el payload de un JWT (base64url) sin verificar la firma. */
function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const part = token.split('.')[1] ?? '';
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * Deriva el proveedor del login federado a partir de los claims ya decodificados
 * del idToken, para etiquetar el rastro de login (`google`/`apple`/`federated`).
 *
 * POR QUÉ aquí: los logins federados (Google/Apple) retornan por este callback,
 * el ÚNICO punto del cliente donde conocemos el proveedor (mismo razonamiento que
 * el comentario de `loginEventHandler.ts` sobre que el login es client-side).
 * Cognito expone el proveedor en `cognito:username` (típicamente `"Google_..."`
 * o `"SignInWithApple_..."`) o en `identities[0].providerName`. Detección trivial
 * a propósito: cualquier caso no reconocido cae en el genérico `federated`. El
 * valor va informativo al servidor, recortado a 40 chars por el backend.
 */
function resolveFederatedProvider(claims: Record<string, unknown>): string {
  const identities = claims.identities;
  const providerName =
    Array.isArray(identities) &&
    identities[0] &&
    typeof (identities[0] as { providerName?: unknown }).providerName === 'string'
      ? ((identities[0] as { providerName: string }).providerName as string)
      : '';
  const cognitoUsername =
    typeof claims['cognito:username'] === 'string' ? (claims['cognito:username'] as string) : '';
  const hint = `${providerName} ${cognitoUsername}`.toLowerCase();
  if (hint.includes('google')) return 'google';
  if (hint.includes('apple')) return 'apple';
  return 'federated';
}

/**
 * Sondea `GET /me/permissions` hasta que la cuenta esté provisionada (el trigger
 * PostConfirmation corre async para usuarios federados nuevos). Tolera 401
 * `unknown_user` reintentando con backoff corto; cualquier otro resultado (ok o
 * error distinto) termina el sondeo.
 */
async function waitForAccountReady(idToken: string): Promise<void> {
  const client = new ApiClient(config.apiUrl, () => idToken);
  const api = createApi(client);
  const delays = [0, 600, 1200, 1800, 2500, 3500];
  for (const delay of delays) {
    if (delay > 0) await new Promise((r) => setTimeout(r, delay));
    try {
      await api.me.permissions();
      return;
    } catch (err) {
      // Solo reintentamos ante 401 (cuenta aún no materializada); el resto corta.
      if (err instanceof ApiError && err.status === 401) continue;
      return;
    }
  }
}

/**
 * Registra (best-effort) el CONSENTIMIENTO LEGAL del alta tras un login
 * FEDERADO (Google/Apple). El flujo por correo+contraseña ya lo registra en
 * `RegisterView` antes de confirmar; el flujo federado no tenía ningún punto
 * donde hacerlo, así que `legal_consent` quedaba sin fila para estas altas y el
 * aviso al administrador mostraba "—" en IP / Idioma / Dispositivo (y ahora
 * País). Al registrarlo aquí, el servidor captura esos datos (IP/user-agent/país
 * derivados del servidor) para el aviso.
 *
 * El AVISO DE ALTA al administrador ("Nueva cuenta registrada") lo ENVÍA ahora
 * el propio backend al recibir este POST de consentimiento (`recordLegalConsent`),
 * que es donde el contexto de sesión ya está completo. Antes lo enviaba el trigger
 * PostConfirmation, que se disparaba ANTES de este POST para el alta federada y el
 * correo salía con esas filas en blanco; el envío se movió al handler del
 * consentimiento para corregirlo.
 *
 * Decisión de de-duplicación: registramos el consentimiento en CADA login
 * federado en lugar de detectar "primer inicio de sesión". `legal_consent` es
 * APPEND-ONLY, así que re-registrar es inofensivo. El aviso al administrador se
 * manda UNA sola vez: el backend lo de-duplica con un marcador atómico
 * (`registration_notice`), de modo que los re-logins federados no reenvían el
 * correo. Detectar el primer login de forma fiable desde el cliente no es trivial
 * (no hay señal directa en el idToken), y este coste extra es despreciable.
 *
 * MEDICIÓN DE CONVERSIÓN (RA-01): el cliente federado NO tiene una señal fiable
 * de "primer ingreso vs. re-login" (el idToken no la expone). Reutilizamos el
 * MISMO mecanismo que ya distingue alta de re-login: el backend devuelve
 * `registration === true` SOLO la primera vez por correo (reclamo atómico
 * one-shot de `registration_notice`). Emitimos `sign_up` únicamente cuando es
 * `true`, de modo que el evento sale EXACTAMENTE una vez por cuenta nueva y un
 * re-login federado no lo dispara. `method` (`google`/`apple`/`federated`) se
 * resuelve en el llamador (único punto del cliente que conoce el proveedor).
 *
 * Best-effort absoluto: cualquier fallo se traga (try/catch) y NUNCA bloquea el
 * login ni la navegación.
 */
async function recordFederatedConsent(email: string, locale: string, method: string): Promise<void> {
  try {
    const docs = await fetchLegalDocuments();
    const versionIds = docs.map((d) => d.version_id).filter((id): id is string => Boolean(id));
    if (versionIds.length === 0) return;
    const result = await recordLegalConsent({ email, versionIds, method: 'signup_checkbox', locale });
    // Solo un alta NUEVA (reclamo autoritativo en el backend) emite la conversión.
    if (result.registration === true) {
      trackEvent('sign_up', { method });
    }
  } catch {
    // Ignorado a propósito: el consentimiento es best-effort y no debe afectar el login.
  }
}

export function AuthCallbackView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const { locale } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  // Evita el doble procesamiento en StrictMode (monta/desmonta/monta): el `code`
  // es de un solo uso y el segundo intercambio fallaría.
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    (async () => {
      try {
        const { tokens, returnTo } = await completeFederatedLogin(window.location.search);
        // `username` para la sesión/analítica: el email del idToken (o el sub).
        const claims = decodeJwtPayload(tokens.idToken);
        const username =
          (typeof claims.email === 'string' && claims.email) ||
          (typeof claims.sub === 'string' && claims.sub) ||
          'usuario';
        // Proveedor del login federado (`google`/`apple`/`federated`). Se resuelve
        // AQUÍ —el único punto del cliente que conoce el proveedor— para usarlo en
        // DOS sitios: el rastro de actividad de `setSession` y la etiqueta `method`
        // de la conversión `sign_up` que emite `recordFederatedConsent`.
        const provider = resolveFederatedProvider(claims);
        // Consentimiento legal del alta federada (best-effort, solo si hay email
        // en los claims). Fire-and-forget: se auto-protege con try/catch y no
        // debe retrasar ni bloquear la entrada del usuario. Emite `sign_up` solo
        // si el backend confirma que es un alta NUEVA (registration === true).
        if (typeof claims.email === 'string' && claims.email.trim() !== '') {
          void recordFederatedConsent(claims.email.trim(), locale, provider);
        }
        // Espera (best-effort) a que la cuenta esté provisionada antes de entrar.
        await waitForAccountReady(tokens.idToken);
        // El rastro de actividad del login lo emite `setSession` (un único disparo,
        // sin doble registro ni fetch a mano) y es best-effort — nunca degrada la
        // sesión ni la navegación.
        setSession(tokens, username, provider);
        const safe = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/';
        navigate(safe, { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : t('authCallback.genericError'));
      }
    })();
  }, [navigate, setSession, t, locale]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-space-md px-margin-mobile py-space-xl text-center min-h-[60vh]">
        <AlertMessage message={error} />
        <a href="/login" className="btn-secondary">
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>{' '}
          {t('authCallback.backToLogin')}
        </a>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Spinner label={t('authCallback.signingIn')} />
    </div>
  );
}
